import type { MeasurementPreferences } from '@kangaroo/shared';

import {
  convertDistanceKm,
  convertSpeedKph,
  convertTemperatureCelsius,
  distanceUnitLabel,
  formatMeasurement,
  formatSpeedKph,
  speedUnitLabel,
  temperatureUnitLabel,
} from './measurements';

export function isSpeedChannelUnit(unit: string | null | undefined) {
  if (!unit) {
    return false;
  }

  const normalized = unit.toLowerCase();
  return normalized === 'km/h' || normalized === 'kph' || normalized === 'km/hr';
}

export function isTemperatureChannelUnit(unit: string | null | undefined) {
  if (!unit) {
    return false;
  }

  const normalized = unit.toLowerCase();
  return normalized === 'c' || normalized === '°c' || normalized === 'celsius';
}

export function isDistanceChannelUnit(unit: string | null | undefined) {
  if (!unit) {
    return false;
  }

  const normalized = unit.toLowerCase();
  return normalized === 'km' || normalized === 'kilometers';
}

export function resolveTelemetryDisplayUnit(unit: string | null | undefined, preferences: MeasurementPreferences) {
  if (isSpeedChannelUnit(unit)) {
    return speedUnitLabel(preferences);
  }

  if (isTemperatureChannelUnit(unit)) {
    return temperatureUnitLabel(preferences);
  }

  if (isDistanceChannelUnit(unit)) {
    return distanceUnitLabel(preferences);
  }

  return unit ?? '';
}

export function convertTelemetryDisplayValue(
  value: number,
  unit: string | null | undefined,
  preferences: MeasurementPreferences,
) {
  if (isSpeedChannelUnit(unit)) {
    return convertSpeedKph(value, preferences);
  }

  if (isTemperatureChannelUnit(unit)) {
    return convertTemperatureCelsius(value, preferences);
  }

  if (isDistanceChannelUnit(unit)) {
    return convertDistanceKm(value, preferences);
  }

  return value;
}

export function formatSpeedFromKph(valueKph: number | null, preferences: MeasurementPreferences, decimals = 1) {
  if (valueKph === null || !Number.isFinite(valueKph)) {
    return 'N/A';
  }

  return `${formatSpeedKph(valueKph, preferences)} ${speedUnitLabel(preferences)}`;
}

export function formatSpeedFromKphShort(valueKph: number | null, preferences: MeasurementPreferences) {
  if (valueKph === null || !Number.isFinite(valueKph)) {
    return `— ${speedUnitLabel(preferences)}`;
  }

  return `${formatMeasurement(convertSpeedKph(valueKph, preferences), 0)} ${speedUnitLabel(preferences)}`;
}

export function formatSpeedDeltaFromKph(deltaKph: number, preferences: MeasurementPreferences, decimals = 1) {
  const converted = convertSpeedKph(deltaKph, preferences);
  const sign = converted >= 0 ? '+' : '';
  return `${sign}${formatMeasurement(converted, decimals)} ${speedUnitLabel(preferences)}`;
}

export function formatTemperatureFromC(valueC: number | null, preferences: MeasurementPreferences, decimals = 0) {
  if (valueC === null || !Number.isFinite(valueC)) {
    return 'N/A';
  }

  return `${formatMeasurement(convertTemperatureCelsius(valueC, preferences), decimals)} ${temperatureUnitLabel(preferences)}`;
}

export function formatDistanceFromKm(valueKm: number | null, preferences: MeasurementPreferences, decimals = 1) {
  if (valueKm === null || !Number.isFinite(valueKm)) {
    return 'N/A';
  }

  return `${formatMeasurement(convertDistanceKm(valueKm, preferences), decimals)} ${distanceUnitLabel(preferences)}`;
}

export function formatTelemetrySeriesValue(
  shortName: string,
  value: number,
  unit: string,
  decimals: number,
  preferences: MeasurementPreferences,
) {
  if (shortName === 'RPM') {
    return Math.round(value).toLocaleString();
  }

  const displayUnit = resolveTelemetryDisplayUnit(unit, preferences);
  const displayValue = convertTelemetryDisplayValue(value, unit, preferences);
  const formatted = displayValue.toFixed(decimals);
  return displayUnit ? `${formatted} ${displayUnit}` : formatted;
}

export function formatOverviewChannelValue(
  label: string,
  value: number,
  unit: string | null,
  preferences: MeasurementPreferences,
) {
  if (label === 'Speed') {
    return formatSpeedFromKph(value, preferences, 1);
  }

  if (label === 'Logger Temp' || label === 'Head Temp') {
    return formatTemperatureFromC(value, preferences, label === 'Head Temp' ? 0 : 1);
  }

  const displayUnit = resolveTelemetryDisplayUnit(unit, preferences);
  const displayValue = convertTelemetryDisplayValue(value, unit, preferences);
  const decimals = label === 'Head Temp' ? 0 : 1;
  return `${displayValue.toFixed(decimals)} ${displayUnit || unit || ''}`.trim();
}
